import { Box, Button, Card, Typography, LinearProgress } from '@mui/material';
import React, { useState, useRef } from 'react';
import { CloudUpload, Image } from '@mui/icons-material';
import Swal from 'sweetalert2';
import { MediaServices } from './MediaServices';

const ImageUpload = ({ onUploadSuccess, currentTab }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                Swal.fire({
                    icon: 'error',
                    title: 'Invalid File',
                    text: 'Please select a valid image file (JPG, PNG, GIF, etc.)',
                });
                return;
            }
            
            // Validate file size (max 10MB)
            const maxSize = 10 * 1024 * 1024; // 10MB in bytes
            if (file.size > maxSize) {
                Swal.fire({
                    icon: 'error',
                    title: 'File Too Large',
                    text: 'Image file must be less than 10MB',
                });
                return;
            }
            
            setSelectedFile(file);
            
            // Create preview
            const reader = new FileReader();
            reader.onload = (e) => {
                setPreviewUrl(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            Swal.fire({
                icon: 'warning',
                title: 'No File Selected',
                text: 'Please select an image file first',
            });
            return;
        }

        setIsUploading(true);
        setUploadProgress(0);

        try {
            // Simulate progress
            const progressInterval = setInterval(() => {
                setUploadProgress((prev) => {
                    if (prev >= 90) {
                        clearInterval(progressInterval);
                        return 90;
                    }
                    return prev + 10;
                });
            }, 500);

            const path = currentTab === 'whatsapp' ? 'whatsapp' : 'all';
            const response = await MediaServices.uploadImage(selectedFile, path);
            
            clearInterval(progressInterval);
            setUploadProgress(100);

            if (response.success === '1' || response.success === 1) {
                Swal.fire({
                    icon: 'success',
                    title: 'Upload Successful',
                    text: 'Image has been uploaded successfully',
                    timer: 1500,
                });
                
                // Reset form
                setSelectedFile(null);
                setPreviewUrl(null);
                setUploadProgress(0);
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
                
                // Refresh list
                if (onUploadSuccess) {
                    onUploadSuccess();
                }
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Upload Failed',
                    text: response.message || 'Failed to upload image',
                });
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Upload Error',
                text: 'Something went wrong while uploading the image',
            });
        } finally {
            setIsUploading(false);
            setUploadProgress(0);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                Swal.fire({
                    icon: 'error',
                    title: 'Invalid File',
                    text: 'Please drop a valid image file',
                });
                return;
            }
            
            const maxSize = 10 * 1024 * 1024;
            if (file.size > maxSize) {
                Swal.fire({
                    icon: 'error',
                    title: 'File Too Large',
                    text: 'Image file must be less than 10MB',
                });
                return;
            }
            
            setSelectedFile(file);
            
            // Create preview
            const reader = new FileReader();
            reader.onload = (e) => {
                setPreviewUrl(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    return (
        <Card sx={{ height: '100%' }}>
            <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', bgcolor: '#f5f5f5' }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                    Upload Image
                </Typography>
            </Box>
            
            <Box sx={{ p: 3 }}>
                {/* Drop Zone */}
                <Box
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                        border: '2px dashed #ccc',
                        borderRadius: 2,
                        p: previewUrl ? 2 : 4,
                        textAlign: 'center',
                        cursor: 'pointer',
                        bgcolor: selectedFile ? '#e3f2fd' : '#fafafa',
                        transition: 'all 0.3s',
                        '&:hover': {
                            borderColor: '#1976d2',
                            bgcolor: '#f5f5f5',
                        },
                    }}
                >
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        accept="image/*"
                        style={{ display: 'none' }}
                    />
                    
                    {previewUrl ? (
                        <Box>
                            <img
                                src={previewUrl}
                                alt="Preview"
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: 150,
                                    objectFit: 'contain',
                                    borderRadius: 4,
                                }}
                            />
                            <Typography sx={{ fontSize: '0.75rem', mt: 1, color: 'text.secondary' }}>
                                {selectedFile.name} ({(selectedFile.size / (1024 * 1024)).toFixed(2)} MB)
                            </Typography>
                        </Box>
                    ) : (
                        <Box>
                            <Image sx={{ fontSize: 48, color: '#aaa', mb: 1 }} />
                            <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                                Drag & drop an image here
                            </Typography>
                            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 1 }}>
                                or click to browse
                            </Typography>
                            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 1 }}>
                                Supported: JPG, PNG, GIF, WebP (Max: 10MB)
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Progress Bar */}
                {isUploading && (
                    <Box sx={{ mt: 2 }}>
                        <LinearProgress 
                            variant="determinate" 
                            value={uploadProgress} 
                            sx={{ height: 8, borderRadius: 4 }}
                        />
                        <Typography sx={{ fontSize: '0.75rem', textAlign: 'center', mt: 1 }}>
                            Uploading... {uploadProgress}%
                        </Typography>
                    </Box>
                )}

                {/* Upload Button */}
                <Button
                    variant="contained"
                    fullWidth
                    sx={{ mt: 3 }}
                    onClick={handleUpload}
                    disabled={!selectedFile || isUploading}
                    startIcon={<CloudUpload />}
                >
                    {isUploading ? 'Uploading...' : 'Upload Image'}
                </Button>
            </Box>
        </Card>
    );
};

export default ImageUpload;
